(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.MyUserPopupReview = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  // A local checklist computed from the merchant's own draft, in the browser.
  // It checks basics it can actually see — the words, the colors as drawn, the
  // fields — and says so. It cannot judge whether an offer is appealing, knows
  // nothing about a store or its shoppers, and never predicts signups or sales.
  //
  // The rules it replaced were an answer key for one sample store: they gave
  // points for "guide", "skincare" and "email", so "guide guide guide" scored
  // 100 while "Get 15% off your first order" was told to name the guide. Every
  // rule below has to hold for any store's pop-up.
  //
  // Text is plain data: render it with textContent/value, never innerHTML.
  const defaults = Object.freeze({
    heading: 'Join our list',
    subheading: 'Get skincare tips and news from Solara.',
    cta: 'Submit',
    decline: 'No thanks',
    storeName: 'Solara',
    thanks: 'Thanks for joining Solara. Your first email is on its way.',
    layout: 'split',
    background: '#faf8f1',
    textColor: '#34472e',
    buttonColor: '#344f36',
    buttonTextColor: '#ffffff',
    headingSize: 32,
    font: 'Georgia',
    firstName: true,
    delay: 0,
    showOnce: true,
    excludeSubscribers: false
  });

  // The preview paints its description, sign-up notice and dismiss link at this
  // opacity (popup-editor.js copies it onto the card as a CSS custom property),
  // so the contrast reported here is the contrast a shopper would see. There
  // must be one number: the checklist once assumed .65 while the preview drew
  // .8, and told the default sample its text "blends into the background".
  const appearance = Object.freeze({ mutedOpacity: 0.8 });

  // Points per check: [pass, could be better]. A Fix earns nothing and caps the
  // total, so an unreadable pop-up can never look like a good one.
  const weights = Object.freeze({
    offer: Object.freeze([30, 12]),
    button: Object.freeze([20, 8]),
    readability: Object.freeze([25, 10]),
    length: Object.freeze([15, 6]),
    exit: Object.freeze([10, 4])
  });
  const fixCap = 49;
  const statusCopy = Object.freeze({
    pass: Object.freeze({ label: 'Pass', symbol: '✓' }),
    improve: Object.freeze({ label: 'Could be better', symbol: '↑' }),
    fix: Object.freeze({ label: 'Fix', symbol: '✕' })
  });
  const rank = { pass: 0, improve: 1, fix: 2 };

  const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
  function plain(value, fallback, limit) {
    if (typeof value !== 'string') return fallback;
    return Array.from(value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim())
      .slice(0, limit).join('');
  }
  function color(value, fallback) {
    if (typeof value !== 'string') return fallback;
    const hex = value.trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/.test(hex)) return hex;
    if (/^#[0-9a-f]{3}$/.test(hex)) return '#' + hex.slice(1).split('').map(c => c + c).join('');
    return fallback;
  }
  function number(value, fallback, min, max) {
    const numeric = typeof value === 'number' ? value
      : typeof value === 'string' && /^-?\d+(?:\.\d+)?$/.test(value.trim()) ? Number(value) : NaN;
    return Number.isFinite(numeric) ? Math.round(Math.max(min, Math.min(max, numeric))) : fallback;
  }
  function boolean(value, fallback) {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  }
  function normalize(input) {
    const source = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
    const get = key => own(source, key) ? source[key] : undefined;
    return {
      heading: plain(get('heading'), defaults.heading, 90),
      subheading: plain(get('subheading'), defaults.subheading, 240),
      cta: plain(get('cta'), defaults.cta, 48),
      decline: plain(get('decline'), defaults.decline, 48),
      storeName: plain(get('storeName'), defaults.storeName, 40),
      thanks: plain(get('thanks'), defaults.thanks, 160),
      layout: ['split', 'simple'].includes(get('layout')) ? get('layout') : defaults.layout,
      background: color(get('background'), defaults.background),
      textColor: color(get('textColor'), defaults.textColor),
      buttonColor: color(get('buttonColor'), defaults.buttonColor),
      buttonTextColor: color(get('buttonTextColor'), defaults.buttonTextColor),
      headingSize: number(get('headingSize'), defaults.headingSize, 18, 48),
      font: ['Georgia', 'Arial', 'Trebuchet MS'].includes(get('font')) ? get('font') : defaults.font,
      firstName: boolean(get('firstName'), defaults.firstName),
      delay: number(get('delay'), defaults.delay, 0, 60),
      showOnce: boolean(get('showOnce'), defaults.showOnce),
      excludeSubscribers: boolean(get('excludeSubscribers'), defaults.excludeSubscribers)
    };
  }

  // ── Color ────────────────────────────────────────────────────────────────
  function rgb(hex) { return [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16)); }
  function hex(channels) {
    return '#' + channels.map(value => Math.round(Math.max(0, Math.min(255, value))).toString(16).padStart(2, '0')).join('');
  }
  function luminance(channels) {
    const linear = channels.map(value => {
      const s = value / 255;
      return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
  }
  // What the eye gets from `front` drawn at `opacity` over `back`.
  function blend(front, back, opacity) {
    const background = rgb(back);
    return hex(rgb(front).map((channel, i) => channel * opacity + background[i] * (1 - opacity)));
  }
  function contrast(front, back, opacity = 1) {
    const a = luminance(rgb(blend(front, back, opacity))), b = luminance(rgb(back));
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  }
  function toHsl(value) {
    const [r, g, b] = rgb(value).map(channel => channel / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min, s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    return [h / 6, s, l];
  }
  function fromHsl([h, s, l]) {
    if (!s) return hex([l, l, l].map(v => v * 255));
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
    const channel = t => {
      t = (t + 1) % 1;
      return t < 1 / 6 ? p + (q - p) * 6 * t : t < 1 / 2 ? q : t < 2 / 3 ? p + (q - p) * (2 / 3 - t) * 6 : p;
    };
    return hex([channel(h + 1 / 3), channel(h), channel(h - 1 / 3)].map(v => v * 255));
  }
  // The smallest lightness change, keeping the merchant's hue, that reaches
  // `target` against `against`. A brand color stays recognisably theirs; only
  // when no shade of it can get there does it fall back to black or white.
  function reach(value, against, target, opacity = 1) {
    if (contrast(value, against, opacity) >= target) return value;
    const [h, s, l] = toHsl(value);
    const options = [];
    for (const end of [0, 1]) {
      let near = l, far = end;
      const edge = fromHsl([h, s, end]);
      if (contrast(edge, against, opacity) < target) continue;
      for (let i = 0; i < 24; i += 1) {
        const mid = (near + far) / 2;
        if (contrast(fromHsl([h, s, mid]), against, opacity) >= target) far = mid; else near = mid;
      }
      options.push({ value: fromHsl([h, s, far]), change: Math.abs(far - l) });
    }
    if (options.length) return options.sort((a, b) => a.change - b.change)[0].value;
    return contrast('#000000', against, opacity) >= contrast('#ffffff', against, opacity) ? '#000000' : '#ffffff';
  }
  const ratioText = ratio => (Math.floor(ratio * 10) / 10).toFixed(1) + ':1';

  // ── Words ────────────────────────────────────────────────────────────────
  function quote(text, max = 60) {
    const chars = Array.from(text);
    return '“' + (chars.length > max ? chars.slice(0, max - 1).join('').trimEnd() + '…' : text) + '”';
  }
  const words = text => text.split(/\s+/).filter(Boolean);
  const cap = text => text.charAt(0).toUpperCase() + text.slice(1);
  const stop = new Set(['with', 'on', 'for', 'when', 'if', 'and', 'or', 'to', 'in', 'at', 'from', 'by', 'every',
    'each', 'your', 'our', 'the', 'a', 'an', 'this', 'that', 'you', 'we', 'of', 'as', 'plus', 'just', 'now', 'today']);
  // "free" followed by something a shopper receives; "free to", "for free" and
  // "gluten-free" are not offers.
  const notAfterFree = new Set([...stop, 'time', 'spirit', 'spirited', 'will', 'range', 'reign', 'rein', 'flow']);
  const things = 'guides?|e-?books?|checklists?|cheat ?sheets?|templates?|lookbooks?|recipes?|patterns?|printables?|workbooks?|courses?|class(?:es)?|lessons?|masterclass|webinars?|samples?|gifts?|vouchers?|coupons?|codes?|playlists?|calendars?|planners?|kits?|minis?|totes?|tutorials?';

  // Finds the most concrete reason to sign up in the copy. Order matters: an
  // amount beats a mention, and a free delivery beats the word "free".
  function findOffer(text) {
    const t = text.toLowerCase();
    let m;
    if ((m = t.match(/(\d{1,2}(?:\.\d)?)\s?%\s*(?:off|discount|savings?)\b/)) || (m = t.match(/(\d{1,2}(?:\.\d)?)\s?percent\s+off\b/))
      || (m = t.match(/\b(?:save|get|take|enjoy|unlock|claim|receive|extra)\s+(?:an?\s+)?(?:extra\s+)?(\d{1,2}(?:\.\d)?)\s?%/))) {
      return { kind: 'percent', phrase: m[1] + '% off', amount: m[1] + '%', tokens: [m[1] + '%', m[1] + ' %', m[1] + ' percent'] };
    }
    if ((m = t.match(/\b(giveaway|sweepstakes|prize draw|chance to win|enter to win|win (?:a|an|the|our))\b/))) {
      return { kind: 'giveaway', phrase: m[1].startsWith('win ') ? 'a chance to win' : m[1], tokens: ['win', 'enter', 'giveaway'] };
    }
    if ((m = t.match(/([$£€])\s?(\d{1,4}(?:[.,]\d{2})?)\s*(off|discount|credit|voucher|coupon|gift card|to spend)\b/))
      || (m = t.match(/\b(?:save|get|take|enjoy|unlock|claim)\s+([$£€])\s?(\d{1,4}(?:[.,]\d{2})?)()/))) {
      const amount = m[1] + m[2];
      // "$10 off" is a discount; "a $25 gift card" is a thing they receive.
      if (m[3] && !/^(off|discount|to spend)$/.test(m[3])) {
        return { kind: 'free', phrase: 'a ' + amount + ' ' + m[3], thing: amount + ' ' + m[3], tokens: [amount, m[3].split(' ').pop()] };
      }
      return { kind: 'money', phrase: amount + ' off', amount, tokens: [amount, m[1] + ' ' + m[2]] };
    }
    if ((m = t.match(/\b(\d{1,4})\s?(dollars?|euros?|pounds?)\s+off\b/))) {
      const amount = m[1] + ' ' + m[2];
      return { kind: 'money', phrase: amount + ' off', amount, tokens: [m[1] + ' ' + m[2]] };
    }
    if (/\bbuy\s+one,?\s+get\s+one\b|\bbogo\b|\b2\s?for\s?1\b|\btwo\s+for\s+one\b/.test(t)) {
      return { kind: 'bogo', phrase: 'buy one, get one', tokens: ['one', 'bogo', '2 for 1', 'deal'] };
    }
    if ((m = t.match(/(?:^|[^\w-])free\s+(?:(?:standard|express|uk|us|worldwide|international|next[-\s]day|2[-\s]day|same[-\s]day)\s+)?(shipping|delivery|postage|returns)\b/))) {
      return { kind: 'shipping', phrase: 'free ' + m[1], tokens: [m[1]] };
    }
    if ((m = t.match(/(?:^|[^\w-])free\s+([a-z][a-z'-]*(?:\s+[a-z][a-z'-]*){0,2})/))) {
      const phrase = [];
      for (const word of m[1].split(/\s+/)) { if (stop.has(word)) break; phrase.push(word); }
      if (phrase.length && !notAfterFree.has(phrase[0])) {
        const thing = phrase.join(' ');
        return { kind: 'free', phrase: 'a free ' + thing, thing, tokens: [phrase[phrase.length - 1], 'free'] };
      }
    }
    if ((m = t.match(new RegExp('\\b(?:our|your|a|an|the|my|this)\\s+((?:[a-z0-9][\\w\'-]*\\s+){0,2}?(?:' + things + '))\\b')))
      || (m = t.match(new RegExp('\\b(' + things + ')\\b')))) {
      const thing = m[1].replace(/\s+/g, ' ');
      const last = thing.split(' ').pop();
      if (!/^(codes?|coupons?|vouchers?)$/.test(last)) return { kind: 'thing', phrase: thing, thing, tokens: [last.replace(/s$/, '')] };
    }
    if ((m = t.match(/\b(early|first|priority|vip|exclusive|members?[-\s]only|insider)\s+(access|dibs|look|pick)\b/))) {
      return { kind: 'access', phrase: m[1] + ' ' + m[2], tokens: [m[2], m[1]] };
    }
    if ((m = t.match(/\bbe\s+(?:the\s+)?first\s+to\s+(know|shop|hear|see|try|get)\b/))) {
      return { kind: 'access', phrase: 'first to ' + m[1], tokens: ['first', 'early'] };
    }
    if ((m = t.match(/\b(pre[-\s]?sales?|presales?|restock alerts?|back[-\s]in[-\s]stock alerts?|launch alerts?)\b/))) {
      return { kind: 'access', phrase: m[1], tokens: [m[1].split(/[-\s]/)[0], 'early', 'first'] };
    }
    if ((m = t.match(/\b(\d{2,5})\s+(?:reward\s+|bonus\s+)?points\b/))) {
      return { kind: 'points', phrase: m[1] + ' points', amount: m[1], tokens: [m[1]] };
    }
    return null;
  }
  // Mentions a reward without saying what it is: "a discount", "exclusive offers".
  function vagueReward(text) {
    const m = text.toLowerCase().match(/\b(discount|savings|deals?|coupon|promo(?:tion)?s?|offers?|rewards?|perks?|treats?|surprises?|codes?)\b/);
    return m ? m[1] : '';
  }
  function suggestButtons(offer, vague) {
    const n = offer && offer.thing ? offer.thing.split(' ').slice(-3).join(' ') : '';
    const short = n.length > 22 ? n.split(' ').pop() : n;
    const list = !offer ? (vague ? ['Get my discount', 'Send me the code'] : ['Get updates', 'Keep me posted'])
      : offer.kind === 'percent' ? ['Get my ' + offer.amount + ' off', 'Unlock ' + offer.amount + ' off', 'Send me my code']
        : offer.kind === 'money' ? ['Get my ' + offer.amount + ' off', 'Claim ' + offer.amount + ' off', 'Send me my code']
          : offer.kind === 'bogo' ? ['Get the deal', 'Send me the code']
            : offer.kind === 'shipping' ? ['Get ' + offer.phrase, 'Unlock ' + offer.phrase]
              : offer.kind === 'free' ? ['Claim my free ' + short, 'Send me the ' + short]
                : offer.kind === 'thing' ? ['Send me the ' + short, 'Get the ' + short, 'Email me the ' + short]
                  : offer.kind === 'access' ? ['Get early access', 'Notify me first']
                    : offer.kind === 'giveaway' ? ['Enter the giveaway', 'Enter to win']
                      : offer.kind === 'points' ? ['Get my ' + offer.amount + ' points', 'Claim my points']
                        : ['Get updates', 'Keep me posted'];
    return list.map(cap).filter(label => Array.from(label).length <= 40);
  }
  const genericButtons = new Set(['submit', 'send', 'ok', 'okay', 'click', 'click here', 'sign up', 'signup', 'sign me up',
    'subscribe', 'subscribe me', 'join', 'join us', 'join in', 'go', 'lets go', "let's go", 'continue', 'enter', 'next', 'yes',
    'done', 'register', 'confirm', 'apply', 'send it', 'submit email', 'count me in', 'im in', "i'm in", 'get started', 'start']);
  // "Sign up now!", "Click here →" and "Submit ›" are the same generic label.
  function buttonWords(label) {
    return label.toLowerCase().replace(/[’`]/g, '\'').replace(/[^\p{L}\p{N}%$£€'\s]/gu, ' ').replace(/\s+/g, ' ').trim()
      .replace(/(?:\s+(?:now|here|today|please))+$/, '').trim();
  }
  // Wording that shames the shopper for saying no ("No thanks, I prefer paying
  // full price"). A plain decline respects the choice and is easier to read.
  const guilt = [/\bfull[-\s]price\b/, /\bpay(?:ing)?\s+more\b/, /\bi\s*(?:'d|would)?\s*(?:rather|prefer)\b/,
    /\bi\s+(?:don'?t|do\s+not|dont)\s+(?:want|like|need|care|deserve)\b/, /\bi\s+(?:hate|love\s+paying|like\s+paying|enjoy\s+paying)\b/,
    /\bnot\s+interested\s+in\s+(?:saving|savings|free|discounts?|deals?|looking)\b/, /\bmiss(?:ing)?\s+out\b/, /\bi'?m\s+(?:not\s+)?(?:rich|fine\s+with|ok\s+with)\b/];

  // ── Checks ───────────────────────────────────────────────────────────────
  function check(id, title, status, parts) {
    const scored = own(weights, id);
    const points = !scored ? 0 : status === 'pass' ? weights[id][0] : status === 'improve' ? weights[id][1] : 0;
    return Object.assign({ id, title, status, label: statusCopy[status].label, symbol: statusCopy[status].symbol,
      scored, points, max: scored ? weights[id][0] : 0, fixes: [], examples: [], parts: [] }, parts);
  }
  function change(label, from, to) { return { label, from: String(from), to: String(to) }; }

  function offerCheck(config) {
    const heading = config.heading, body = config.subheading;
    const inHeading = findOffer(heading), inBody = inHeading ? null : findOffer(body);
    const offer = inHeading || inBody;
    const base = { field: 'heading', editLabel: 'Edit heading', target: 'offer', offer };
    const examples = ['Get 10% off your first order', 'Free shipping on your first order', 'A free guide: 5 ways to use it', 'Early access to new arrivals'];
    if (!heading && !body) return check('offer', 'Clear reason to sign up', 'fix', Object.assign(base, {
      summary: 'Nothing says why to sign up.',
      detail: 'The heading and description are both empty, so the pop-up asks for an email without a reason.',
      examples
    }));
    if (inHeading) return check('offer', 'Clear reason to sign up', 'pass', Object.assign(base, {
      summary: 'The heading names what they get.',
      detail: 'Shoppers see ' + quote(offer.phrase) + ' first. Set up the email or code that delivers it before the pop-up goes live.'
    }));
    if (inBody) return check('offer', 'Clear reason to sign up', 'improve', Object.assign(base, {
      summary: 'Put the offer in the heading.',
      detail: 'The description offers ' + quote(offer.phrase) + ', but the heading' + (heading ? ', ' + quote(heading) + ',' : '') +
        ' doesn’t. The heading is read first, so lead with what they get.',
      examples: [cap(offer.phrase) + (offer.kind === 'percent' || offer.kind === 'money' ? ' your first order' : '')]
    }));
    const vague = vagueReward(heading + ' ' + body);
    const many = /s$/.test(vague) && vague !== 'discount';
    const asks = /\b(join|subscribe|sign\s*up|newsletter|list|stay in touch|updates|news|in the loop|in the know)\b/i.test(heading);
    return check('offer', 'Clear reason to sign up', 'improve', Object.assign(base, {
      summary: vague ? 'Say what the ' + vague + (many ? ' are.' : ' is.') : 'Say what shoppers get.',
      detail: (vague
        ? 'The copy mentions ' + (many ? vague : 'a ' + vague) + ' but not what ' + (many ? 'they are' : 'it is') + '.'
        : heading ? quote(heading) + (asks ? ' asks shoppers to join but doesn’t say what they get.' : ' doesn’t say what shoppers get for signing up.')
          : 'There’s no heading, and the description doesn’t name a reason to sign up.') +
        ' Name one concrete reason\u00a0— one you can actually give.',
      examples
    }));
  }

  function buttonCheck(config, offer, vague) {
    const label = config.cta, core = buttonWords(label);
    const echoes = !!offer && offer.tokens.some(token => core.includes(token.toLowerCase()));
    const base = { field: 'cta', editLabel: 'Edit button', target: 'cta' };
    const suggestions = () => suggestButtons(offer, vague).filter(text => text.toLowerCase() !== label.toLowerCase())
      .slice(0, 3).map((text, i) => ({ id: 'button:' + i, label: 'Use ' + quote(text), patch: { cta: text }, changes: [change('Button text', label || '(empty)', text)] }));
    if (!core) return check('button', 'Button says what happens', 'fix', Object.assign(base, {
      summary: 'The button has no words.',
      detail: 'Without a label, shoppers can’t tell what the button does.',
      fixes: suggestions()
    }));
    if (genericButtons.has(core) || (words(core).length === 1 && !echoes)) return check('button', 'Button says what happens', 'improve', Object.assign(base, {
      summary: quote(label, 30) + ' doesn’t say what they get.',
      detail: 'A button that names the result is clearer than ' + quote(label, 30) + '.' +
        (offer ? ' Your offer is ' + quote(offer.phrase) + ', so the button can say it again.' : ' Pick one that fits, or write your own.'),
      fixes: suggestions()
    }));
    return check('button', 'Button says what happens', 'pass', Object.assign(base, {
      summary: echoes ? 'It repeats your offer.' : 'It says what happens.',
      detail: echoes ? quote(label, 40) + ' repeats ' + quote(offer.phrase) + ', so shoppers know what the click gets them.'
        : quote(label, 40) + ' is an action, not just “Submit”.' + (offer ? ' Naming ' + quote(offer.phrase) + ' in it would tie it to your offer.' : '')
    }));
  }

  function readabilityCheck(config) {
    const muted = appearance.mutedOpacity;
    const bigHeading = config.headingSize >= 24;
    const parts = [
      { id: 'heading', label: 'Heading', front: config.textColor, back: config.background, opacity: 1, need: bigHeading ? 3 : 4.5, floor: 3,
        why: bigHeading ? 'large text needs 3:1' : 'text under 24px needs 4.5:1' },
      { id: 'description', label: 'Description and small print', front: config.textColor, back: config.background, opacity: muted, need: 4.5, floor: 3,
        why: 'drawn at ' + Math.round(muted * 100) + '% opacity; small text needs 4.5:1' },
      { id: 'label', label: 'Button text', front: config.buttonTextColor, back: config.buttonColor, opacity: 1, need: 4.5, floor: 3,
        why: 'button words need 4.5:1' },
      { id: 'button', label: 'Button shape', front: config.buttonColor, back: config.background, opacity: 1, need: 3, floor: 0,
        why: 'against the pop-up it needs 3:1 to look like a button' }
    ].map(part => {
      const ratio = contrast(part.front, part.back, part.opacity);
      const status = ratio >= part.need ? 'pass' : ratio < part.floor ? 'fix' : 'improve';
      return Object.assign(part, { ratio, ratioText: ratioText(ratio), status, drawn: blend(part.front, part.back, part.opacity) });
    });
    const small = config.headingSize < 22;
    const worst = parts.reduce((status, part) => rank[part.status] > rank[status] ? part.status : status, small ? 'improve' : 'pass');
    const failing = parts.filter(part => part.status !== 'pass');
    const fixes = [];
    const text = parts[0].status !== 'pass' || parts[1].status !== 'pass';
    if (text) {
      // One text color draws the heading, description and small print, so one
      // change fixes them together, measured at the opacity the preview uses.
      // The background is never touched: it is the merchant's brand. Text that
      // was merely faint gets the smallest change that passes; text that had
      // all but vanished gets a comfortably dark (or light) shade of its hue.
      const next = reach(config.textColor, config.background, parts[1].ratio < 2 ? 7 : 4.6, muted);
      const darker = luminance(rgb(next)) < luminance(rgb(config.textColor));
      if (next !== config.textColor) fixes.push({ id: 'readability:text', label: (darker ? 'Darken' : 'Lighten') + ' the text',
        patch: { textColor: next }, changes: [change('Text color', config.textColor, next)] });
    }
    if (parts[2].status !== 'pass') {
      // Button words are almost always white or near-black. Keep a label that
      // only needs a nudge close to its own color instead.
      const plain = ['#ffffff', '#000000'].sort((a, b) => contrast(b, config.buttonColor) - contrast(a, config.buttonColor))[0];
      const next = parts[2].ratio < 2 ? plain : reach(config.buttonTextColor, config.buttonColor, 4.6);
      if (next !== config.buttonTextColor) fixes.push({ id: 'readability:label', label: 'Make the button text stand out',
        patch: { buttonTextColor: next }, changes: [change('Button text color', config.buttonTextColor, next)] });
    }
    if (parts[3].status !== 'pass') {
      // The shape is the button against the card. The merchant's own text color
      // usually makes a button that stands out, with the background color for
      // its words; only when that pair is weak does it shade the button itself.
      const own = contrast(config.textColor, config.background) >= 3 && contrast(config.background, config.textColor) >= 4.5;
      const button = own ? config.textColor : reach(config.buttonColor, config.background, 3.1);
      const label = own ? config.background
        : contrast(config.buttonTextColor, button) >= 4.5 ? config.buttonTextColor : reach(config.buttonTextColor, button, 4.6);
      const patch = { buttonColor: button }, changes = [change('Button color', config.buttonColor, button)];
      if (label !== config.buttonTextColor) { patch.buttonTextColor = label; changes.push(change('Button text color', config.buttonTextColor, label)); }
      if (button !== config.buttonColor) fixes.push({ id: 'readability:button', label: own ? 'Use your text color for the button' : 'Make the button stand out', patch, changes });
    }
    if (small) fixes.push({ id: 'readability:size', label: 'Make the heading 24px', patch: { headingSize: 24 },
      changes: [change('Heading size', config.headingSize + 'px', '24px')] });
    const describe = part => part.label + ': ' + part.ratioText + (part.status === 'fix' ? ', nearly invisible' : '') + '\u00a0— ' + part.why + '.';
    // Lead with the worst part: its sentence is the summary and its setting is
    // the one "Edit colors" opens.
    const lead = failing.slice().sort((a, b) => rank[b.status] - rank[a.status])[0];
    const says = {
      heading: ['The text can’t be read.', 'The text could stand out more.'],
      description: ['The text can’t be read.', 'The small text could stand out more.'],
      label: ['The button’s words can’t be read.', 'The button’s words could stand out more.'],
      button: ['', 'The button blends into the pop-up.']
    };
    const field = lead ? { heading: 'textColor', description: 'textColor', label: 'buttonTextColor', button: 'buttonColor' }[lead.id]
      : small ? 'headingSize' : 'textColor';
    const more = failing.length > 1 ? ' (' + failing.length + ' pairs)' : '';
    return check('readability', 'Easy to read', worst, {
      field, editLabel: field === 'headingSize' ? 'Edit heading size' : 'Edit colors',
      target: 'readability:' + (failing.length ? failing.map(part => part.id).join(',') : small ? 'heading' : 'all'),
      summary: lead ? says[lead.id][lead.status === 'fix' ? 0 : 1] + more : small ? 'The heading is small.' : 'Every color pair is easy to read.',
      detail: (failing.length ? failing.map(describe).join(' ') : 'Measured as drawn, every color pair meets its contrast target.') +
        (small ? ' The heading is ' + config.headingSize + 'px, close to the 14px text under it; 24px or more sets it apart.' : ''),
      parts, fixes
    });
  }

  function lengthCheck(config) {
    const headingWords = words(config.heading), chars = Array.from(config.subheading).length;
    const counts = {};
    headingWords.forEach(word => { const key = word.toLowerCase().replace(/[^\p{L}\p{N}]/gu, ''); if (key.length > 2) counts[key] = (counts[key] || 0) + 1; });
    const repeated = Object.keys(counts).find(key => counts[key] >= 3);
    const notes = [];
    if (headingWords.length > 8) notes.push('The heading is ' + headingWords.length + ' words; 8 or fewer is quicker to read.');
    if (repeated) notes.push('The heading repeats ' + quote(repeated) + ' ' + counts[repeated] + ' times.');
    if (chars > 120) notes.push('The description is ' + chars + ' characters; one sentence under 120 is easier to take in.');
    const headingIssue = headingWords.length > 8 || !!repeated;
    const field = headingIssue ? 'heading' : 'subheading';
    return check('length', 'Short enough', notes.length ? 'improve' : 'pass', {
      field, editLabel: headingIssue ? 'Edit heading' : 'Edit description', target: field,
      summary: repeated ? 'The heading repeats itself.' : headingIssue ? 'The heading runs long.' : chars > 120 ? 'The description runs long.'
        : !config.heading && !config.subheading ? 'Nothing to trim yet.' : 'Quick to read.',
      detail: notes.length ? notes.join(' ')
        : !config.heading && !config.subheading ? 'Add a heading first.'
          : 'Heading: ' + headingWords.length + ' word' + (headingWords.length === 1 ? '' : 's') + '. Description: ' + chars + ' characters.',
      chars, headingWords: headingWords.length
    });
  }

  function fieldsCheck(config) {
    return check('fields', 'Quick to fill in', 'pass', {
      field: 'firstName', editLabel: 'Edit sign-up fields', target: 'fields',
      summary: config.firstName ? 'Name and email.' : 'Email only.',
      detail: config.firstName
        ? 'Asking for a first name is fine if your emails use it to greet people. If they won’t, email alone is one less field.'
        : 'One field: the quickest sign-up there is.',
      fixes: config.firstName ? [{ id: 'fields:email', label: 'Ask for email only', patch: { firstName: false }, optional: true,
        changes: [change('Fields', 'First name and email', 'Email only')] }] : []
    });
  }

  function exitCheck(config) {
    const decline = config.decline;
    const base = { field: 'decline', editLabel: 'Edit dismiss link', target: 'decline' };
    const plainFixes = ['No thanks', 'Maybe later'].filter(text => text !== decline).map((text, i) => ({
      id: 'exit:' + i, label: 'Use ' + quote(text), patch: { decline: text }, changes: [change('Dismiss link', decline || '(empty)', text)] }));
    if (!decline) return check('exit', 'A clear way out', 'improve', Object.assign(base, {
      summary: 'There’s no “No thanks” link.',
      detail: 'Shoppers can still close it with ✕, but a visible way to say no is easier to find.',
      fixes: plainFixes
    }));
    const lower = decline.toLowerCase().replace(/[’`]/g, '\'');
    if (guilt.some(pattern => pattern.test(lower))) return check('exit', 'A clear way out', 'improve', Object.assign(base, {
      summary: 'The dismiss link makes people feel bad.',
      detail: quote(decline, 48) + ' shames shoppers for saying no. A plain decline respects the choice.',
      fixes: plainFixes
    }));
    return check('exit', 'A clear way out', 'pass', Object.assign(base, {
      summary: 'Saying no is easy.',
      detail: quote(decline, 48) + ' lets shoppers decline plainly, and ✕ closes it too.'
    }));
  }

  function timing(config) {
    return [config.delay === 0 ? 'Shows right away' : 'Shows after ' + config.delay + ' second' + (config.delay === 1 ? '' : 's'),
      config.showOnce ? 'once per visit' : 'on every page view',
      config.excludeSubscribers ? 'skips people on your list' : 'includes people already on your list'].join(' · ');
  }

  function evaluate(input) {
    const config = normalize(input);
    const offer = offerCheck(config);
    const vague = vagueReward(config.heading + ' ' + config.subheading);
    const checks = [offer, buttonCheck(config, offer.offer, vague), readabilityCheck(config), lengthCheck(config), fieldsCheck(config), exitCheck(config)];
    const raw = checks.reduce((total, row) => total + row.points, 0);
    const fixes = checks.filter(row => row.status === 'fix').length;
    const score = fixes ? Math.min(raw, fixCap) : raw;
    return {
      score, max: 100, raw, capped: fixes > 0 && raw > fixCap,
      counts: { pass: checks.filter(row => row.status === 'pass').length, improve: checks.filter(row => row.status === 'improve').length, fix: fixes },
      offer: offer.offer, timing: timing(config), checks
    };
  }

  function findFix(input, id) {
    if (typeof id !== 'string') return null;
    for (const row of evaluate(input).checks) {
      const fix = row.fixes.find(item => item.id === id);
      if (fix) return fix;
    }
    return null;
  }
  function applyFix(input, id) {
    const config = normalize(input);
    const fix = findFix(config, id);
    return fix ? normalize(Object.assign({}, config, fix.patch)) : config;
  }

  return Object.freeze({ defaults, appearance, weights, fixCap, normalize, evaluate, findFix, applyFix, contrast, findOffer });
});
